import { BusinessObjectBase } from './base';
import { testResultSchema } from '@schemas/business-objects/test-result';
import type { Student } from './student';
import type { Term } from './term';
import type { TestArticle } from './test-article';
import type { User } from './user';
import type {
  CalendarDate,
  Id,
  TestCycle,
  TestStage,
  TestType,
} from '../base-types';

export interface TestResultData {
  readonly id: Id;
  readonly type?: TestType;
  readonly cycle?: TestCycle;
  readonly stage?: TestStage;
  readonly date?: CalendarDate;
  readonly achievedOralReadingFluency?: string;
  readonly workingOralReadingFluency?: string;
  readonly achievedCorrectWordsPerMinute?: number;
  readonly achievedErrors?: number;
  readonly workingCorrectWordsPerMinute?: number;
  readonly workingErrors?: number;
  readonly tattumStart?: string;
  readonly consonantSoundScore?: number;
  readonly vowelSoundScore?: number;
  readonly firstFailedLevel?: number;
  readonly decodingLevel1PartA?: number;
  readonly decodingLevel1PartB?: number;
  readonly decodingLevel2?: number;
  readonly decodingLevel3?: number;
  readonly decodingLevel4?: number;
  readonly decodingLevel5?: number;
  readonly decodingLevel6?: number;
  readonly decodingLevel7?: number;
  readonly decodingLevel8?: number;
  readonly decodingLevel9?: number;
  readonly decodingLevel10?: number;
  readonly decodingLevel11?: number;
  readonly decodingLevel12?: number;
  readonly decodingLevel13?: number;
  readonly decodingLevel14?: number;
  readonly decodingLevel15?: number;
  readonly studentId: Id;
  readonly student?: Student;
  readonly appUserId: Id;
  readonly appUser?: User;
  readonly achievedArticleId: Id;
  readonly achievedArticle?: TestArticle;
  readonly workingArticleId: Id;
  readonly workingArticle?: TestArticle;
  readonly termId: Id;
  readonly term?: Term;
}

export interface TestResult extends Readonly<TestResultData> {}
export class TestResult extends BusinessObjectBase {
  static readonly entityName = 'TestResult' as const;
  static readonly plural = 'test-results';
  static schema = testResultSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TestResult[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TestResult(data));
  }

  static async findOne(filter?: any): Promise<TestResult | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TestResult(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TestResult> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TestResult(data);
  }

  static async create(data: Omit<TestResultData, 'id'>): Promise<TestResult> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TestResult(result);
  }

  static async createMany(
    data: Omit<TestResultData, 'id'>[],
  ): Promise<TestResult[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TestResult(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TestResultData>,
  ): Promise<TestResult> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TestResult(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
